import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

import { sizingClass, TONE_CLASS } from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';
import type { ComponentFn } from '@littlebird/action-cards-view/react';
import type { ComponentType } from 'react';

type IconProps = Parameters<ComponentFn<typeof catalog, 'Icon'>>[0]['props'];
type CardIconName = IconProps['name'];
type Glyph = ComponentType<{ className?: string }>;

function PlusMinus({ className }: { className?: string }) {
  return (
    <span className={cn('relative inline-block', className)}>
      <Icons.plus className="absolute inset-x-0 top-0 h-3/4 w-full" />
      <Icons.minus className="absolute inset-x-0 bottom-0 h-1/2 w-full" />
    </span>
  );
}

// Solid variants of `Circle` draw the same glyph with a `fill-current` class; see
// `FILLED_NAMES` below
export const ICON_GLYPH: Record<CardIconName, Glyph> = {
  check: Icons.check,
  x: Icons.cancel,
  plus: Icons.plus,
  warning: Icons.warn,
  clock: Icons.clock,
  calendar: Icons.calendar,
  paperclip: Icons.paperclip,
  pin: Icons.mapPin,
  'plus-minus': PlusMinus,
  external: Icons.externalLink,
  'chevron-down': Icons.chevronDown,
  'chevron-right': Icons.chevronRight,
  branch: Icons.gitBranch,
  meet: Icons.googleMeet,
  comment: Icons.speech,
  retry: Icons.rotateCw,
  send: Icons.paperPlane,
  open: Icons.arrowUpRight,
  hash: Icons.hash,
  at: Icons.atSign,
  flag: Icons.flag,
  circle: Icons.circle,
  'circle-dashed': Icons.circleDashed,
  'circle-filled': Icons.circle,
  'circle-half': Icons.circleHalf,
  'circle-check': Icons.circleCheck,
  'circle-x': Icons.circleX,
  // Linear draws triage as two crossing arrows
  triage: Icons.arrowRightLeft,
  list: Icons.list,
  // A graduated bar count reads as a level, the way Linear's own priority glyphs do:
  // urgent gets the full signal rather than a separate alert shape
  'priority-urgent': Icons.signal,
  'priority-high': Icons.signalHigh,
  'priority-medium': Icons.signalMedium,
  'priority-low': Icons.signalLow,
  dot: Icons.dot,
};

const FILLED_NAMES = new Set<CardIconName>(['circle-filled', 'dot']);

// `color` (a provider or priority hex) overrides `tone` through inline style, the
// way ProviderIcon and Select's option dot already carry a caller-given colour
export const Icon: ComponentFn<typeof catalog, 'Icon'> = ({ props }) => {
  const Glyph = ICON_GLYPH[props.name];
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex size-[calc(16*var(--ac-u))] shrink-0',
        sizingClass(props.sizing),
        TONE_CLASS[props.tone ?? 'default']
      )}
      style={props.color ? { color: props.color } : undefined}
    >
      <Glyph
        className={cn(
          'size-full',
          FILLED_NAMES.has(props.name) && 'fill-current'
        )}
      />
    </span>
  );
};
